import { useEffect, useMemo, useState } from 'react'

import { Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  CheckCircleFill,
  PencilFill,
  PlusCircleFill,
  TrashFill,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'

import { CustomButton } from '../../../components/CustomButton'

import CategoriesService from '../../../services/database/CategoriesService'

import { useOrderTable } from '../../../hooks/useOrderTable'

import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import { Center, Container, GroupX } from './styles'
import { ModalCreate } from './ModalCreate'
import { CustomSpinner } from '../../../components/CustomSpinner'

export function Categories() {
  const [categories, setCategories] = useState([])
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true)

  const [categorySearch, setCategorySearch] = useState('')

  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false)

  const filteredCategories = useMemo(() => {
    return categories.filter((category) =>
      category.categoria.toLowerCase().includes(categorySearch.toLowerCase()),
    )
  }, [categorySearch, categories])

  const {
    check,
    handleOrganize,
    sortedArray: sortedCategories,
  } = useOrderTable(filteredCategories, 'categoria', 'asc')

  async function loadCategories() {
    try {
      setIsCategoriesLoading(true)

      const data = await CategoriesService.getAll()

      setCategories(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao carregar as categorias!')
    } finally {
      setIsCategoriesLoading(false)
    }
  }

  useEffect(() => {
    loadCategories()
  }, [])

  return (
    <>
      <Container>
        <h1>Categorias</h1>

        {isCategoriesLoading ? (
          <Center>
            <CustomSpinner size={48} />

            <h1>Carregando...</h1>
          </Center>
        ) : (
          <>
            <GroupX>
              <CustomButton
                title="Cadastrar Categoria"
                color="#0d9a04"
                onClick={() => setIsModalCreateOpen(true)}
                styles={{
                  marginLeft: 0,
                }}
              >
                <PlusCircleFill />
              </CustomButton>

              <CustomButton
                title="Recarregar"
                color="#04519a"
                onClick={loadCategories}
                styles={{
                  marginLeft: 0,
                }}
              >
                <ArrowClockwise />
              </CustomButton>
            </GroupX>

            <Form.Control
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Pesquise..."
              style={{
                marginTop: 20,
              }}
            />

            <Table>
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('id_categoria')}>
                    <span>ID</span>
                    <CaretUp color={check('id_categoria', 'asc')} />
                    <CaretDown color={check('id_categoria', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('categoria')}>
                    <span>Nome</span>
                    <CaretUp color={check('categoria', 'asc')} />
                    <CaretDown color={check('categoria', 'desc')} />
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((category) => (
                  <Category
                    key={category.id_categoria}
                    category={category}
                    loadCategories={loadCategories}
                  />
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Container>

      {isModalCreateOpen && (
        <ModalCreate
          isOpen={isModalCreateOpen}
          onClose={() => setIsModalCreateOpen(false)}
          loadCategories={loadCategories}
        />
      )}
    </>
  )
}

function Category({ category, loadCategories }) {
  const [categoryName, setCategoryName] = useState(category.categoria)

  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState(false)
  const [isUpdateRequestLoading, setIsUpdateRequestLoading] = useState(false)

  const [isEditMode, setIsEditMode] = useState(true)

  async function handleUpdateCategory(id) {
    try {
      setIsUpdateRequestLoading(true)

      await CategoriesService.update(id, { name: categoryName })

      loadCategories()
      toast.success('Categoria atualizada com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao atualizar a categoria!')
    } finally {
      setIsUpdateRequestLoading(false)
    }
  }

  async function handleDeleteCategory(id) {
    try {
      setIsDeleteRequestLoading(true)

      await CategoriesService.delete(id)

      loadCategories()
      toast.success('Categoria deletada com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao deletar a categoria!')
    } finally {
      setIsDeleteRequestLoading(false)
    }
  }

  return (
    <tr>
      <td>{category.id_categoria}</td>
      <td>
        <Form.Control
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          disabled={isEditMode}
        />
      </td>
      <td
        style={{
          display: 'flex',
        }}
      >
        <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
          <CustomButton
            isLoading={!isEditMode && isUpdateRequestLoading}
            color={isEditMode ? 'orange' : 'green'}
            onClick={() =>
              isEditMode
                ? setIsEditMode(false)
                : handleUpdateCategory(category.id_categoria)
            }
          >
            {isEditMode ? <PencilFill /> : <CheckCircleFill />}
          </CustomButton>
        </OverlayTrigger>

        <OverlayTrigger overlay={<Tooltip>Deletar</Tooltip>}>
          <CustomButton
            color="red"
            isLoading={isDeleteRequestLoading}
            onClick={() => handleDeleteCategory(category.id_categoria)}
          >
            <TrashFill />
          </CustomButton>
        </OverlayTrigger>
      </td>
    </tr>
  )
}
